import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useUserDataContext } from '../contexts/UserDataContext.jsx';
import { Bell, Clock, Check, X, ChevronLeft, ChevronRight, Star, Calendar, X as CloseIcon } from 'lucide-react';
import ProductDetailModal from './UserDashboard/components/ProductDetailModal.jsx';
import { processRoutineForDisplay } from '../utils/routineUtils.js';

const UserDashboard = () => {
  const { user, userData, updateUserData, loading } = useUserDataContext();
  const [reminders, setReminders] = useState({ AM: false, PM: false, Weekly: false });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showReminders, setShowReminders] = useState(false);
  const dateSliderRef = useRef(null);

  const progress = userData?.progress || {};

  const processedRoutine = useMemo(() => {
    if (!userData || !userData.routine) return {};
    return processRoutineForDisplay(userData.routine);
  }, [userData?.routine]);

  useEffect(() => {
    if (userData) {
      setReminders(userData.reminders || { AM: false, PM: false, Weekly: false });
    }
  }, [userData?.reminders]);

  const handleReminderToggle = async (time) => {
    const newReminders = { ...reminders, [time]: !reminders[time] };
    setReminders(newReminders);
    await updateUserData({ reminders: newReminders });

    if (newReminders[time] && typeof Notification !== 'undefined' && Notification.permission !== 'granted') {
      Notification.requestPermission();
    }
  };

  const handleProgressUpdate = async (productName, time, status) => {
    const dateKey = formatDate(selectedDate);
    const newProgress = JSON.parse(JSON.stringify(progress));

    if (!newProgress[dateKey]) newProgress[dateKey] = {};
    if (!newProgress[dateKey][time]) newProgress[dateKey][time] = {};

    if (newProgress[dateKey][time][productName] === status) {
      delete newProgress[dateKey][time][productName];
    } else {
      newProgress[dateKey][time][productName] = status;
    }

    await updateUserData({ progress: newProgress });
  };

  const formatDate = (date) => date.toISOString().split('T')[0];

  const [selectedDate, setSelectedDate] = useState(new Date());

  const dateRange = useMemo(() => {
    const start = new Date();
    start.setDate(start.getDate() - 90);
    const end = new Date();
    end.setDate(end.getDate() + 90);
    
    let dates = [];
    for (let dt = new Date(start); dt <= end; dt.setDate(dt.getDate() + 1)) {
      dates.push(new Date(dt));
    }
    return dates;
  }, []);

  useEffect(() => {
    if (dateSliderRef.current) {
        const todayEl = dateSliderRef.current.querySelector(`[data-date="${formatDate(selectedDate)}"]`);
        if (todayEl) {
            const containerWidth = dateSliderRef.current.offsetWidth;
            const elementLeft = todayEl.offsetLeft;
            const elementWidth = todayEl.offsetWidth;
            
            dateSliderRef.current.scrollTo({
                left: elementLeft - (containerWidth / 2) + (elementWidth / 2),
                behavior: 'smooth'
            });
        }
    }
  }, [selectedDate]); 


  if (loading || !user || !userData) {
    return (
        <div className="w-full h-screen flex flex-col justify-center items-center bg-gray-50">
            <div className="animate-pulse w-16 h-16 bg-green-200 rounded-full mb-4"></div>
            <p className="font-heading text-lg text-gray-500 font-medium">Loading your routine...</p>
        </div>
    );
  }
  
  const userName = user.displayName || user.email?.split('@')[0] || 'Guest';
  const avatarUrl = user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=ecfdf5&color=059669`;

  const getRoutineForSelectedDate = () => {
    const dayOfWeek = selectedDate.toLocaleDateString('en-US', { weekday: 'long' });
    const routineForDay = (processedRoutine && processedRoutine[dayOfWeek]) ? processedRoutine[dayOfWeek] : {};

    return {
      AM: routineForDay.AM?.sort((a, b) => a.step - b.step) || [],
      PM: routineForDay.PM?.sort((a, b) => a.step - b.step) || [],
      Weekly: routineForDay.Weekly?.sort((a, b) => a.step - b.step) || [],
    };
  };

  const { AM: morningRoutine, PM: eveningRoutine, Weekly: weeklyRoutine } = getRoutineForSelectedDate();
  
  const hasRoutine = userData && userData.routine && Object.keys(userData.routine).length > 0;
  const showDashboard = userData.assessmentCompleted || hasRoutine;

  const RoutineCard = ({ product, time, index, status, onUpdate, onClick }) => {
    const isCompleted = status === 'completed';
    const isSkipped = status === 'skipped';

    return (
      <div 
        onClick={onClick} 
        className={`
          relative group overflow-hidden rounded-2xl border transition-all duration-300 cursor-pointer
          ${isCompleted ? 'bg-green-50/50 border-green-100' : isSkipped ? 'bg-gray-50 border-gray-100 opacity-70' : 'bg-white border-gray-100 hover:border-green-200 hover:shadow-lg shadow-sm'}
        `}
      >
        <div className="p-4 flex items-center gap-4">
          {/* Image & Step Badge */}
          <div className="relative shrink-0">
            <div className="w-20 h-20 rounded-xl bg-gray-50 flex items-center justify-center overflow-hidden border border-gray-100">
              <img 
                src={product.image || 'https://placehold.co/100x100/f1f5f9/334155?text=Product'} 
                alt={product.name} 
                className="w-full h-full object-contain p-2 mix-blend-multiply" 
              />
            </div>
            <div className="absolute -top-2 -left-2 w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center text-xs font-bold shadow-md z-10 border-2 border-white">
              {product.step || index + 1}
            </div>
          </div>

          {/* Content */}
          <div className="flex-grow min-w-0">
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-0.5">{product.category}</p>
            <h4 className={`font-heading font-bold text-gray-900 truncate pr-2 ${isCompleted ? 'text-green-800' : ''}`}>
              {product.name}
            </h4>
            {isCompleted && <span className="text-xs font-medium text-green-600 flex items-center mt-1"><Check size={12} className="mr-1"/> Completed</span>}
            {isSkipped && <span className="text-xs font-medium text-gray-400 flex items-center mt-1">Skipped</span>}
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2 shrink-0 z-20">
            <button 
              onClick={(e) => { e.stopPropagation(); onUpdate(product.name, time, 'completed'); }} 
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 active:scale-95 shadow-sm
                ${isCompleted 
                  ? 'bg-green-500 text-white shadow-green-200' 
                  : 'bg-white border border-gray-100 text-gray-300 hover:border-green-500 hover:text-green-500'
                }`}
            >
              <Check size={20} strokeWidth={3} />
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); onUpdate(product.name, time, 'skipped'); }} 
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 active:scale-95 shadow-sm
                ${isSkipped 
                  ? 'bg-yellow-100 text-yellow-600 border border-yellow-200' 
                  : 'bg-white border border-gray-100 text-gray-300 hover:border-yellow-400 hover:text-yellow-400'
                }`}
            >
              <X size={20} strokeWidth={3} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderReminderToggle = (time, label) => {
    const isEnabled = reminders[time];
    return (
        <div 
            onClick={() => handleReminderToggle(time)}
            className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${isEnabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 hover:bg-gray-100'}`}
        >
            <span className={`font-heading font-medium text-sm ${isEnabled ? 'text-green-800' : 'text-gray-700'}`}>{label}</span>
            <div className={`relative w-10 h-5 rounded-full transition-colors ${isEnabled ? 'bg-green-500' : 'bg-gray-300'}`}>
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isEnabled ? 'translate-x-5' : 'translate-x-0'}`}></span>
            </div>
        </div>
    );
  };

  const renderRoutineSection = (title, time, routine) => {
    const dateKey = formatDate(selectedDate);
    const dayProgress = progress[dateKey]?.[time] || {};
    
    // Dynamic styles based on AM/PM/Weekly
    const headerColors = {
        'AM': 'bg-sky-100 text-sky-700',
        'PM': 'bg-indigo-100 text-indigo-700',
        'Weekly': 'bg-rose-100 text-rose-700'
    };
    const badgeColor = headerColors[time] || headerColors['AM'];

    return (
      <div className="mb-10">
        <div className="flex items-center justify-between mb-5 px-1">
            <div className="flex items-center gap-3">
                <span className={`h-8 w-1 rounded-full ${time === 'AM' ? 'bg-sky-400' : time === 'PM' ? 'bg-indigo-400' : 'bg-rose-400'}`}></span>
                <h3 className="font-heading text-xl font-bold text-gray-800">{title}</h3>
                <span className={`font-heading px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full ${badgeColor}`}>
                    {time === 'Weekly' ? 'Weekly' : `${time} Ritual`}
                </span>
            </div>
            <div className="text-xs font-medium text-gray-400">
                {routine.length} Steps
            </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {routine.length > 0 ? routine.map((product, index) => (
             <RoutineCard 
                key={index} 
                index={index} 
                product={product} 
                time={time} 
                status={dayProgress[product.name]} 
                onUpdate={handleProgressUpdate}
                onClick={() => setSelectedProduct(product)}
             />
          )) : (
            <div className="border-2 border-dashed border-gray-200 rounded-2xl p-8 flex flex-col items-center justify-center text-center bg-gray-50/50">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-3 shadow-sm">
                 <Clock className="text-gray-300" size={20} />
              </div>
              <p className="font-heading text-gray-500 font-medium">Nothing scheduled for this time.</p>
              <p className="text-xs text-gray-400 mt-1">Enjoy your free time!</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50/50">
        {/* Header Background Decoration */}
        <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-emerald-50/80 to-transparent -z-10 pointer-events-none"></div>

      <main className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          
          {/* Top Navigation / Profile Header */}
          <div className="flex items-center justify-between mb-10 pt-2">
            <div className='flex items-center gap-4'>
                <Link to="/profile" className="group relative">
                    <div className="w-14 h-14 rounded-full bg-white p-1 shadow-md ring-1 ring-gray-100 transition-transform group-hover:scale-105">
                        <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover rounded-full" />
                    </div>
                    <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></div>
                </Link>
                <div>
                  <h1 className="font-heading text-2xl font-extrabold text-gray-900 tracking-tight">Hi, {userName}</h1>
                  <p className="font-heading text-sm text-gray-500 font-medium">Your skin looks glowing today!</p>
                </div>
            </div>
            <div className='flex items-center gap-3'>
                <div className='flex items-center gap-2 bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-100'>
                    <Star size={16} className='text-amber-400 fill-amber-400' />
                    <span className='font-heading font-bold text-gray-700 text-sm'>{userData.points || 0} pts</span>
                </div>
                
                {/* Reminder Icon & Popover */}
                <div className="relative">
                    <button 
                        onClick={() => setShowReminders(!showReminders)}
                        className="relative w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-sm border border-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
                    >
                        <Bell size={20} />
                        {(reminders.AM || reminders.PM || reminders.Weekly) && (
                            <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full"></span>
                        )}
                    </button>

                    {showReminders && (
                        <div className="absolute top-14 right-0 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 p-4 animate-in fade-in zoom-in-95 duration-200">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="font-heading font-bold text-gray-800">Reminders</h4>
                                <CloseIcon size={18} className="text-gray-400 cursor-pointer" onClick={() => setShowReminders(false)} />
                            </div>
                            <p className="font-heading text-sm text-gray-500 mb-4">Set push notifications for your routines.</p>
                            <div className="space-y-3">
                                {renderReminderToggle('AM', 'Morning Routine')}
                                {renderReminderToggle('PM', 'Evening Routine')}
                                {renderReminderToggle('Weekly', 'Weekly Ritual')}
                            </div>
                        </div>
                    )}
                </div>
            </div>
          </div>

          {showDashboard ? (
            <>
             {/* Infinite Date Slider */}
              <div className="mb-10 relative group">
                {/* Fade edges */}
                <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-gray-50/50 to-transparent z-10 pointer-events-none"></div>
                <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-gray-50/50 to-transparent z-10 pointer-events-none"></div>

                <div 
                    ref={dateSliderRef} 
                    className="flex items-center space-x-3 overflow-x-auto pb-6 pt-2 px-4 scrollbar-hide snap-x snap-mandatory" 
                >
                  {dateRange.map((date, index) => {
                    const isSelected = formatDate(selectedDate) === formatDate(date);
                    const isToday = formatDate(new Date()) === formatDate(date);
                    
                    return (
                        <div
                            key={index}
                            data-date={formatDate(date)}
                            onClick={() => setSelectedDate(date)}
                            className={`
                                snap-center flex-shrink-0 w-[4.5rem] h-24 rounded-2xl cursor-pointer transition-all duration-300 flex flex-col justify-center items-center relative overflow-hidden
                                ${isSelected 
                                    ? 'bg-gray-900 text-white shadow-lg scale-105 transform' 
                                    : 'bg-white text-gray-500 border border-gray-100 hover:border-gray-300 hover:bg-gray-50'
                                }
                            `}
                        >
                            {isToday && !isSelected && <span className="absolute top-2 right-2 w-1.5 h-1.5 bg-red-400 rounded-full"></span>}
                            
                            <p className={`font-heading text-xs font-bold uppercase tracking-wider mb-1 ${isSelected ? 'text-gray-400' : 'text-gray-400'}`}>
                                {date.toLocaleDateString('en-US', { weekday: 'short' })}
                            </p>
                            <p className={`font-heading text-2xl font-bold ${isSelected ? 'text-white' : 'text-gray-800'}`}>
                                {date.getDate()}
                            </p>
                            {isSelected && <div className="absolute bottom-0 left-0 right-0 h-1 bg-green-500"></div>}
                        </div>
                    )
                  })}
                </div>
              </div>

              {/* Routines */}
              <div className="space-y-6">
                {renderRoutineSection('Morning Routine', 'AM', morningRoutine)}
                {renderRoutineSection('Evening Routine', 'PM', eveningRoutine)}
                {weeklyRoutine.length > 0 && renderRoutineSection('Weekly Ritual', 'Weekly', weeklyRoutine)}
              </div>

              <div className="mt-12 mb-8">
                <Link 
                    to="/progress-tracking-dashboard" 
                    className="group relative w-full flex items-center justify-center px-6 py-4 border border-transparent text-base font-bold rounded-2xl text-white bg-gray-900 hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                    <span>View Full Progress</span>
                    <ChevronRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </>
          ) : (
            <div className="bg-white p-10 rounded-3xl shadow-xl border border-gray-100 text-center mt-10">
                <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Calendar className="text-emerald-600" size={32} />
                </div>
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-3">Welcome to your Dashboard</h2>
              <p className="font-heading text-gray-500 mb-8 leading-relaxed max-w-sm mx-auto">
                Your journey to better skin starts here. Take our quick assessment to build your personalized routine.
              </p>
              <Link to="/skin-assessment-questionnaire" className="inline-flex items-center justify-center bg-emerald-600 text-white font-bold py-4 px-8 rounded-xl hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-200 active:scale-95">
                Start Assessment
              </Link>
            </div>
          )}
        </div>
      </main>
      <ProductDetailModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
    </div>
  );
};

export default UserDashboard;